/**
 * Filename Parser for SLAM Sports AI Pitch Video Metadata Extraction
 *
 * Parses filenames in format: {PitchType}_{CameraAngle}.mov
 * Examples: Curveball_CF.mov, Fastball_HP.mov, Slider_1B.mov
 */

// Pitch type mappings
const PITCH_TYPE_MAP = {
  'fastball': { code: 'FF', fullName: 'Fastball' },
  'curveball': { code: 'CU', fullName: 'Curveball' },
  'slider': { code: 'SL', fullName: 'Slider' },
  'changeup': { code: 'CH', fullName: 'Changeup' },
  'cutter': { code: 'FC', fullName: 'Cutter' },
  'sinker': { code: 'SI', fullName: 'Sinker' },
  'splitter': { code: 'FS', fullName: 'Splitter' },
  'sweeper': { code: 'SW', fullName: 'Sweeper' },
};

// Camera angle mappings with analysis focus
const CAMERA_ANGLE_MAP = {
  'cf': {
    description: 'Center Field',
    analysisFocus: 'Glove position, timing/rhythm, grip adjustment visibility, overall mechanics'
  },
  'hp': {
    description: 'Home Plate',
    analysisFocus: 'Arm slot angle, release point, head movement, forward trunk tilt'
  },
  '1b': {
    description: 'First Base',
    analysisFocus: 'Body positioning (RHP open side), grip visibility, contralateral trunk tilt'
  },
  '3b': {
    description: 'Third Base',
    analysisFocus: 'Body positioning (LHP open side), grip visibility, contralateral trunk tilt'
  },
};

/**
 * Parse a filename to extract pitch metadata
 *
 * @param {string} filename - The filename to parse (e.g., "Curveball_CF.mov")
 * @returns {Object|null} PitchMetadata object or null if parsing fails
 */
export function parseFilename(filename) {
  if (!filename) return null;

  // Remove extension and normalize
  const baseName = filename.replace(/\.[^/.]+$/, '');

  // Try to match pattern: PitchType_CameraAngle
  const parts = baseName.split('_');

  if (parts.length < 2) {
    // Try other common separators
    const altParts = baseName.split(/[-\s]/);
    if (altParts.length >= 2) {
      parts.length = 0;
      parts.push(...altParts);
    }
  }

  if (parts.length < 2) return null;

  const pitchTypePart = parts[0].toLowerCase();
  const cameraPart = parts[parts.length - 1].toLowerCase();

  const pitchInfo = PITCH_TYPE_MAP[pitchTypePart];
  const cameraInfo = CAMERA_ANGLE_MAP[cameraPart];

  if (!pitchInfo || !cameraInfo) {
    // Try partial matching for pitch type
    const matchedPitch = Object.entries(PITCH_TYPE_MAP).find(
      ([key]) => pitchTypePart.includes(key) || key.includes(pitchTypePart)
    );

    const matchedCamera = Object.entries(CAMERA_ANGLE_MAP).find(
      ([key]) => cameraPart.includes(key) || key.includes(cameraPart)
    );

    if (!matchedPitch && !matchedCamera) return null;

    return {
      pitchType: matchedPitch?.[1].fullName || parts[0],
      cameraAngle: cameraPart.toUpperCase(),
      pitchTypeCode: matchedPitch?.[1].code || 'UNK',
      cameraDescription: matchedCamera?.[1].description || cameraPart.toUpperCase(),
      cameraAnalysisFocus: matchedCamera?.[1].analysisFocus || 'General mechanics analysis',
    };
  }

  return {
    pitchType: pitchInfo.fullName,
    cameraAngle: cameraPart.toUpperCase(),
    pitchTypeCode: pitchInfo.code,
    cameraDescription: cameraInfo.description,
    cameraAnalysisFocus: cameraInfo.analysisFocus,
  };
}

/**
 * Generate a context string for Gemini analysis from metadata
 *
 * @param {Object} metadata - The parsed pitch metadata
 * @param {string} filename - Original filename for reference
 * @returns {string} Formatted context string for injection into prompt
 */
export function generateMetadataContext(metadata, filename) {
  return `[VIDEO CONTEXT]
Filename: ${filename}
Expected Pitch Type: ${metadata.pitchType} (${metadata.pitchTypeCode})
Camera Angle: ${metadata.cameraAngle} (${metadata.cameraDescription})
Analysis Focus for This Angle: ${metadata.cameraAnalysisFocus}
---`;
}

/**
 * Check if a filename appears to contain pitch metadata
 *
 * @param {string} filename - The filename to check
 * @returns {boolean} true if the filename contains recognizable pitch/camera patterns
 */
export function hasMetadataPattern(filename) {
  const lower = filename.toLowerCase();
  const hasPitchType = Object.keys(PITCH_TYPE_MAP).some(type => lower.includes(type));
  const hasCameraAngle = Object.keys(CAMERA_ANGLE_MAP).some(angle => lower.includes(angle));
  return hasPitchType && hasCameraAngle;
}

// Alias for V2 compatibility
export const parseFilenameForMetadata = parseFilename;
